import React, { createContext, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import cartReducer from '../../reducers/cartReducer';
import addCartItem from '../../actions/ADD_CART_ITEM';
import setCartItem from '../../actions/SET_CART_ITEM';
import delCartItem from '../../actions/DEL_CART_ITEM';
import reloadCartItems from '../../actions/RELOAD_CART_ITEMS';
import { LoadingContext } from './LoadingContext';
import { node } from 'prop-types';
import useIdempotency from '../../hooks/useIdempotency';

export const CartContext = createContext({});

// The initial state of the cart context
const INITIAL_STATE = []; // eslint-disable-line no-undef

// The app context provider component
const CartContextProvider = ({ children }) => {
    // defaultCartContext is returned by the server on the initial render
    const [checkoutData, setCheckoutData] = useState({});
    const [cartState, cartDispatch] = useReducer(cartReducer, INITIAL_STATE);
    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    const [cartUpdating, setCartUpdating] = useState(false);
    const idempotency = useIdempotency();
    const reloadLineItems = useCallback((lineItems = []) => {
        // Reload all items
        cartDispatch(
            reloadCartItems(lineItems.map((lineItem) => ({
                lineItemId: lineItem.id,
                inventoryLevelId: lineItem.inventoryLevelId,
                title: lineItem.title,
                imageUrl: lineItem.imageUrl ?? null,
                quantity: lineItem.currentQuantity,
                price: lineItem.totalDiscountedPrice,
                availableForSale: lineItem.availableForSale,
                availableQuantity: lineItem.availableQuantity,
                sku: lineItem.sku,
                handle: lineItem.productHandle ?? null,
                bypassNegativeQuantity: lineItem.bypassNegativeQuantity,
            })))
        );
    }, []);
    // Add item to checkout (if there's a valid session)
    const addLineItem = useCallback(async (inventoryLevelId, quantity, lineItems, onFinish = () => {}) => {
        try {
            startLoading();
            setCartUpdating(true);
            let body = null;
            if (lineItems) {
                body = JSON.stringify({ lineItems });
            } else {
                body = JSON.stringify({ inventoryLevelId,
                    quantity });
            }
            const response = await fetch(new URL('/api/checkouts/lineItems', process.env.APP_URL), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                ...body && { body },
            });
            finishLoading();
            setCartUpdating(false);
            let data;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.indexOf('application/json') !== -1) {
                data = await response.json();
            }
            if (!response.ok) {
                if (data?.error) {
                    throw new Error(data.error);
                }
                throw new Error('Failed to add item to cart.');
            }
            if (data?.checkout) {
                if (Array.isArray(lineItems) && lineItems.length > 0) {
                    cartDispatch(reloadLineItems(data.checkout.LineItems));
                } else {
                    const newLineItem =
                        data.checkout.LineItems
                            .filter((lineItem) => lineItem.inventoryLevelId === parseInt(inventoryLevelId))
                            ?.at(0) ?? null;
                    // Add item
                    setCheckoutData(data.checkout);
                    if (newLineItem)
                        cartDispatch(addCartItem(inventoryLevelId, quantity, newLineItem.title, newLineItem.imageUrl, newLineItem.totalDiscountedPrice, newLineItem.availableQuantity, newLineItem.availableForSale, newLineItem.sku, newLineItem.productHandle, newLineItem.id, newLineItem.bypassNegativeQuantity));
                }
            } else {
                throw new Error('Couldn\'t update cart.');
            }
            onFinish();
            return { success: true };
        } catch (e) {
            console.error(e);
            finishLoading();
            return {
                success: false,
                error: e.message,
            };
        }
    }, [startLoading, finishLoading, reloadLineItems]);
    // Set a checkout item quantity if the item exists in the checkout and the session checkout is valid
    const setLineItem = useCallback(async (inventoryLevelId, quantity) => {
        try {
            startLoading();
            setCartUpdating(true);
            const response = await fetch(new URL('/api/checkouts/lineItems', process.env.APP_URL), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ inventoryLevelId,
                    quantity }),
            });
            finishLoading();
            setCartUpdating(false);
            if (!response.ok)
                throw new Error('Failed to update item.');
            const data = await response.json();
            if (data.checkout) {
                // Update item
                setCheckoutData(data.checkout);
                cartDispatch(setCartItem(inventoryLevelId, quantity));
            }
        } catch (e) {
            console.error(e);
            finishLoading();
        }
    }, [startLoading, finishLoading]);
    // Delete an item from the checkout
    const delLineItem = useCallback(async (lineItemId) => {
        try {
            startLoading();
            setCartUpdating(true);
            const response = await fetch(new URL('/api/checkouts/lineItems/' + lineItemId, process.env.APP_URL), {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            finishLoading();
            setCartUpdating(false);
            if (!response.ok)
                throw new Error('Failed to delete item from cart.');
            const data = await response.json();
            if (data.checkout) {
                // Delete item
                setCheckoutData(data.checkout);
                cartDispatch(delCartItem(lineItemId));
            }
        } catch (e) {
            console.error(e);
            finishLoading();
        }
    }, [startLoading, finishLoading]);
    // Fetch checkout data
    const fetchCheckoutData = useCallback(async () => {
        try {
            const response = await fetch(new URL('api/checkouts', process.env.APP_URL));
            const data = await response.json();
            if (data.checkout) {
                setCheckoutData(data.checkout);
                reloadLineItems(data.checkout.LineItems);
            }
        } catch (e) {
            console.error(e);
        }
    }, [ reloadLineItems ]);
    const addProductsToCart = useCallback(async (handles) => {
        try {
            startLoading();
            setCartUpdating(true);
            const body = { handles };
            // Execute the request
            const data = await idempotency.execute(
                new URL('/api/checkouts/products', process.env.APP_URL),
                body,
                'POST',
                {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            );

            // If there's a checkout, update the cart

            if (data?.checkout) {
                setCheckoutData(data.checkout);
                reloadLineItems(data.checkout.LineItems);
            } else {
                throw new Error('Couldn\'t update cart.');
            }
            return { success: true };
        } catch (e) {
            console.info(e);
            return {
                success: false,
                error: e.message,
            };
        } finally {
            finishLoading();
            setCartUpdating(false);
        }
    }, [checkoutData, finishLoading, reloadLineItems, startLoading]);
    const contextValue = useMemo(() => ({
        cartState,
        checkoutData,
        cartUpdating,
        addLineItem,
        setLineItem,
        delLineItem,
        reloadLineItems,
        fetchCheckoutData,
        addProductsToCart,
    }), [cartState, checkoutData, cartUpdating, addLineItem, setLineItem, delLineItem, reloadLineItems, fetchCheckoutData, addProductsToCart]);
    return (<CartContext.Provider
        value={contextValue}>
        {children}
    </CartContext.Provider>);
};

CartContextProvider.propTypes = {
    children: node,
};

export default CartContextProvider;
